/**
 * Trims transparent or purely white borders off an image.
 * @returns Trimmed image URI.
 */
 export function trimImage(uri: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const element = new Image();
    element.src = uri;
    element.onload = function () {
      try {
        resolve(trimImageByLoadedElement(element).toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    }
    element.onerror = reject;
  })
}

/**
 * @param element Element to render.
 * @param optimize Whether to scale down the canvas size if required to prevent app freeze.
 * @returns Trimmed canvas. 
 */
function trimImageByLoadedElement(element: HTMLImageElement, optimize = false): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = element.width;
  canvas.height = element.height;

  if (optimize) {
    optimizeCanvas(canvas);
  }

  const context = canvas.getContext('2d');
  context.drawImage(element, 0, 0, canvas.width, canvas.height);

  return trimCanvas(context);
}

function optimizeCanvas(canvas: HTMLCanvasElement) {
  const maxSize = 480;
  if (canvas.width <= maxSize && canvas.height <= maxSize) {
    return;
  }

  const aspectRatio = canvas.width / canvas.height;
  if (!aspectRatio) {
    return canvas;
  }

  if (canvas.width > canvas.height) {
    canvas.width = maxSize;
    canvas.height = maxSize / aspectRatio;
  } else {
    canvas.height = maxSize;
    canvas.width = maxSize * aspectRatio;
  }

  return canvas;
}

/**
 * @returns A new, trimmed canvas.
 */
function trimCanvas(context: CanvasRenderingContext2D): HTMLCanvasElement {
  //  offsets
  let left = 0, top = 0, right = 0, bottom = 0;
  let leftHit = false, topHit = false, rightHit = false, bottomHit = false;

  function isBorder(x: number, y: number, width: number, height: number) {
    const { data } = context.getImageData(x, y, width, height);

    for (let i = 0; i < data.length; i += 4) {
      const isBorderPx = (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) || data[i + 3] === 0;
      
      if (!isBorderPx) {
        return false;
      }
    }

    return true;
  }

  do {
    if (!leftHit) {
      left++;
      const scanHeight = context.canvas.height - top - bottom;
      leftHit = !isBorder(left, top, 1, scanHeight);
    }
    if (!topHit) {
      top++;
      const scanWidth = context.canvas.width - left - right;
      topHit = !isBorder(left, top, scanWidth, 1);
    }
    if (!rightHit) {
      right++;
      const scanHeight = context.canvas.height - top - bottom;
      rightHit = !isBorder(context.canvas.width - right, top, 1, scanHeight);
    }
    if (!bottomHit) {
      bottom++;
      const scanWidth = context.canvas.width - left - right;
      bottomHit = !isBorder(left, context.canvas.height - bottom, scanWidth, 1);
    }
  } while (!leftHit || !topHit || !rightHit || !bottomHit);

  return cropCanvas(context.canvas, left, top, right, bottom);
}

/**
 * @param left Left offset, in px
 * @param top Top offset, in px
 * @param right Right offset, in px
 * @param bottom Bottom offset, in px
 * @returns A new, cropped canvas.
 */
function cropCanvas(canvas: HTMLCanvasElement, left: number, top: number, right: number, bottom: number) {
  const cropped = document.createElement('canvas');
  cropped.width = canvas.width - right - left;
  cropped.height = canvas.height - bottom - top;

  cropped.getContext('2d').drawImage(canvas, -left, -top);
  return cropped;
}